import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaArrowLeft } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import logo from '../assets/logo.png';
import './VerifyEmail.css';
import api from '../services/api';

const VerifyEmail = () => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) return setError('Please enter your email');
    try {
      setIsLoading(true);
      setError('');
      const response = await api.post('/users/send-otp/', { email });
      const data = response.data;
      if (data.success) {
        setCodeSent(true);
        setSuccess('Verification code sent to your email');
        setCountdown(60);
      } else {
        throw new Error(data.message || 'Failed to send code');
      }
    } catch (error) {
      setError(error.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (verificationCode.length !== 6) return;
    try {
      setIsVerifying(true);
      setError('');
      const response = await api.post('/users/verify-otp/', { email, otp: verificationCode });
      const data = response.data;
      if (data.success) {
        setSuccess('Email verified successfully!');
        navigate('/register-step-1', { state: { email, verified: true } });
      } else {
        throw new Error(data.message || 'Verification failed');
      }
    } catch (error) {
      setError(error.message || 'Failed to verify code');
    } finally {
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendCode = async (e) => {
    e.preventDefault();
    if (countdown > 0) return;
    setError('Failed to resend code. Please try again.');
  };

  return (
    <Layout>
      <div className="verify-email-page-container">
      <div className="verify-email-split-screen">
        <div className="verify-email-left-panel">
          <div className="verify-access-content">
            <img src={logo} alt="UniTrade Logo" className="verify-logo" />
            <h2>Verify Your Access</h2>
            <p>A secure marketplace starts with verified users. Please check your email for a verification code.</p>
          </div>
        </div>
        <div className="verify-email-right-panel">
          <div className="verify-form-container">
            <button type="button" className="back-button" onClick={() => navigate(-1)}>
              <FaArrowLeft /> Back
            </button>

            <h3>Verify Your Email</h3>
            <p className="verify-subtitle">
              {codeSent ? 'Enter the 6-digit code sent to your email.' : 'We will send a verification code to your email.'}
            </p>

            {error && <div className="alert-danger">{error}</div>}
            {success && !error && <div className="alert-success">{success}</div>}

            <form onSubmit={codeSent ? handleVerifyCode : handleSendCode} className="verify-form">
              {!codeSent ? (
                <div className="input-group-verify">
                  <label htmlFor="email">Email address</label>
                  <div className="input-with-icon">
                    <FaEnvelope className="input-icon" />
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="input-group-verify">
                  <label htmlFor="verificationCode">Verification Code</label>
                  <div className="verification-code-container">
                    <input
                      id="verificationCode"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={verificationCode}
                      onChange={e => {
                        const value = e.target.value.replace(/\D/g, '');
                        setVerificationCode(value);
                      }}
                      placeholder="Enter 6-digit code"
                      disabled={isVerifying}
                      required
                      className="verification-code-input"
                      autoComplete="one-time-code"
                    />
                    <div className="verification-code-display">
                      {Array(6).fill(0).map((_, index) => (
                        <div 
                          key={index} 
                          className={`verification-digit ${index < verificationCode.length ? 'filled' : ''}`}
                        >
                          {index < verificationCode.length ? verificationCode[index] : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="button-group-verify">
                {!codeSent ? (
                  <button type="submit" className="verify-btn-main" disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Send Verification Code'}
                  </button>
                ) : (
                  <button type="submit" className="verify-btn-main" disabled={isVerifying || verificationCode.length !== 6}>
                    {isVerifying ? 'Verifying...' : 'Verify & Proceed'}
                  </button>
                )}

                {codeSent && (
                  <button
                    type="button"
                    className="resend-btn"
                    onClick={handleResendCode}
                    disabled={countdown > 0 || isLoading}
                  >
                    {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    </Layout>
  );
}

export default VerifyEmail;
